<script lang="ts">
	import { onMount, onDestroy } from 'svelte';
	import { profileStore, user, avatarUrlStore, profileLoading } from '$lib/user';
	import { createStore } from '$lib/chat/Notification.store';
	import { supabaseClient } from '$lib/supabase';
	import type { ExtendedNotification } from '$lib/chat/Notification.store';
	import { writable } from 'svelte/store';

	import avatars from '$lib/components/common/AvatarLibrary.json';
	import { paraLogin, paraLogout } from '$lib/user/para/functions';

	// let moreToggle = false;
	const topLink =
		'm-auto text-lg 2xl:text-xl font-semibold text-center cursor-pointer mx-4 no-underline hover:no-underline font-quicksand';
	const buttonBase = 'button shadow-lg rounded-full py-2 px-4 bg-bloomPurpleDark ';
	const navButton =
		'nav-button xl:mx-2 px-3 py-2 text-black text-xs font-bold border-none bg-none cursor-pointer transition-all duration-300 hover:bg-white hover:bg-opacity-20 hover:rounded-lg xl:mx-0.5 xl:px-2 xl:py-2 xl:text-sm 2xl:text-base 2xl:mx-1 xl:font-bold 4xl:text-lg 3xl:mx-1.5';
	const navButtonWhite =
		'nav-button lg:mx-2 lg:px-2 xl:px-6 py-2 text-white text-base font-bold border-none bg-none cursor-pointer transition-all duration-300 hover:bg-white hover:bg-opacity-20 hover:rounded-lg xl:mx-0.5 xl:px-2 xl:py-2 lg:text-xs xl:text-base 2xl:mx-1 xl:font-bold 4xl:text-lg 3xl:mx-1.5';
	// const findButton =
	// 	buttonBase + 'bg-primary text-gray-100 border-2 border-solid border-bloomBlueNormal';
	// let profileToggle = false;
	let aboutToggle = false;
	let avatarUrl: string;
	let profileDropdownOpen = false;

	// Ensure profile dropdown is closed when user changes
	$: if ($user) {
		profileDropdownOpen = false;
	}

	const onSignout = async () => {
		const { paraLogout } = await import('$lib/user/para/functions');
		await paraLogout();
	};

	const getRandomAvatar = () => {
		return avatars[Math.floor(Math.random() * avatars.length)];
	};

	// Subscribe to avatarUrlStore
	const unsubscribe = avatarUrlStore.subscribe((url) => {
		avatarUrl = url || getRandomAvatar(); // Update the avatarUrl only when it changes
	});

	const toggleProfile = async () => {
		profileDropdownOpen = !profileDropdownOpen;
		if (profileDropdownOpen) {
			try {
				// const newNotifications = await getUnreadUserNotifs();
				// console.log('newNotifications', newNotifications);
			} catch (error) {
				console.error('Error fetching unread notifications:', error);
				// Handle the error appropriately
			}
		}
		// console.log('profileToggle', profileToggle);
	};

	// function formatNumber(wallet: string) {
	// 	// Check if wallet is null or undefined
	// 	if (wallet === null || wallet === undefined) {
	// 		return ''; // or handle the null/undefined case in another way
	// 	}
	// 	const firstSixDigits = wallet.slice(0, 6);
	// 	const lastFourDigits = wallet.slice(-4);
	// 	return `${firstSixDigits}...${lastFourDigits}`;
	// }

	async function onLogout() {
		await paraLogout();
	}

	const toggleAbout = async () => {
		aboutToggle = !aboutToggle;
	};

	// Handle clicking outside to close dropdowns
	const handleClickOutside = (event: MouseEvent) => {
		const target = event.target as HTMLElement;

		// Close profile dropdown if clicking outside
		if (
			profileDropdownOpen &&
			!target.closest('#dropdownMenu') &&
			!target.closest('[data-profile-button]')
		) {
			profileDropdownOpen = false;
		}

		// Close about dropdown if clicking outside
		if (aboutToggle && !target.closest('[data-about-dropdown]')) {
			aboutToggle = false;
		}
	};

	let notificationsStore: ReturnType<typeof createStore>;
	let unreadCount = writable(0);
	let notifications: ExtendedNotification[] = [];

	onMount(() => {
		if (!$profileStore.id) {
			return;
		}
		avatarUrl = $profileStore.avatar_url || getRandomAvatar();

		// Ensure profile dropdown is closed on mount
		profileDropdownOpen = false;

		// Add click outside listener
		document.addEventListener('click', handleClickOutside);

		notificationsStore = createStore($profileStore.id);

		// Subscribe to the notifications store to get updates to the unread count
		const unsubscribe = notificationsStore.subscribe((value) => {
			notifications = value;
			const count = notifications.filter((notification) => !notification.read).length;
			unreadCount.set(count);
			// console.log('Navbar unreadCount:', count, notifications);
		});

		const memberChat = supabaseClient
			.channel('schema-db-changes')
			.on(
				'postgres_changes',
				{
					event: 'INSERT',
					schema: 'public',
					table: 'notifications'
				},
				async (payload) => {
					const username = $profileStore.id;
					if (!username) return;

					if (
						payload.new.replied_to_username === username ||
						payload.new.mentioned.includes(username)
					) {
						// Refresh the notifications store instead of replacing it
						notificationsStore.refresh();
					}
				}
			)
			.subscribe();

		return () => {
			unsubscribe();
			memberChat.unsubscribe();
			document.removeEventListener('click', handleClickOutside);
		};
	});

	onDestroy(() => {
		unsubscribe();
	});
</script>

{#if !$user}
	<nav
		class="navbar-menu flex justify-end bg-gradient-to-r from-[#00A3B9] to-[#16E17F] opacity-90 px-2 py-1 w-full lg:w-[75%] lg:-ml-14 xl:ml-auto 4xl:w-[60%] lg:pr-0 rounded-l-full"
	>
		<!-- Menu items -->
		<div class="hidden lg:flex items-center justify-center w-full font-black">
			<a
				on:click={() => {
					if (aboutToggle) aboutToggle = false;
				}}
				on:keyup={() => {
					if (aboutToggle) aboutToggle = false;
				}}
				class={navButton}
				href="/communityposts">Community Media</a
			>
			<div class="relative" data-about-dropdown>
				<div
					class={navButton}
					on:click={toggleAbout}
					on:keyup={toggleAbout}
					role="button"
					aria-pressed="false"
					tabindex="0"
				>
					About
				</div>
				{#if aboutToggle}
					<div
						class="absolute top-12 bg-gradient-to-r from-[#00A3B9] to-[#16E17F] opacity-90 rounded-b-2xl z-50"
					>
						<a
							on:click={toggleAbout}
							on:keyup={toggleAbout}
							class="block pb-2 {navButton}"
							href="/vision">Vision</a
						>
						<a
							on:click={toggleAbout}
							on:keyup={toggleAbout}
							class="block pb-2 {navButton}"
							href="/engage/communityCalls">Events</a
						>
						<a
							on:click={toggleAbout}
							on:keyup={toggleAbout}
							class="block pb-2 {navButton}"
							href="/team">Team</a
						>
						<a
							on:click={toggleAbout}
							on:keyup={toggleAbout}
							class="block pb-2 {navButton}"
							href="/wiki">Knowledge Wiki</a
						>
					</div>
				{/if}
			</div>
			<a
				on:click={() => {
					if (aboutToggle) aboutToggle = false;
				}}
				on:keyup={() => {
					if (aboutToggle) aboutToggle = false;
				}}
				class={navButton}
				href="/marketplace">Marketplace</a
			>
			<a
				on:click={() => {
					if (aboutToggle) aboutToggle = false;
				}}
				on:keyup={() => {
					if (aboutToggle) aboutToggle = false;
				}}
				class={navButton}
				href="/bloomers-without-borders">Volunteer</a
			>
			<a
				on:click={() => {
					if (aboutToggle) aboutToggle = false;
				}}
				on:keyup={() => {
					if (aboutToggle) aboutToggle = false;
				}}
				class={navButton}
				href="/donate">Donate</a
			>
			<a
				on:click={() => {
					if (aboutToggle) aboutToggle = false;
				}}
				on:keyup={() => {
					if (aboutToggle) aboutToggle = false;
				}}
				class="nav-button px-3 py-2 text-black text-xs font-bold rounded-lg cursor-pointer transition-all duration-300 hover:bg-white hover:bg-opacity-20 xl:mx-0.5 xl:px-2 xl:py-2 xl:text-sm 2xl:text-base 2xl:mx-1 xl:font-bold 4xl:text-lg 3xl:mx-1.5 border-2 mr-5 xl:mr-10 3xl:mr-24 border-cosmicMagenta400"
				href="/engage/localBlooms">Local Blooms</a
			>
			{#if $user}
				<div
					role="button"
					aria-pressed="false"
					tabindex="0"
					on:click={onLogout}
					on:keyup|preventDefault={(el) => {
						if (el.key === 'Enter') onLogout();
					}}
					class="block col-span-4 px-3 py-2 hover:bg-cosmicPurplebg"
				>
					Sign Out
				</div>
			{:else}
				<button
					on:click={() => {
						if (aboutToggle) aboutToggle = false;
					}}
					on:keyup={() => {
						if (aboutToggle) aboutToggle = false;
					}}
					class="nav-button mx-2 px-2 lg:px-3 xl:px-4 py-2 text-base font-bold border-none bg-none cursor-pointer transition-all duration-300 hover:bg-cosmicPurple800 lg:mx-0.5 lg:py-2 lg:text-sm xl:text-base 2xl:mx-1 lg:font-bold bg-cosmicPurple600 rounded-lg text-white"
					on:click={() => paraLogin()}
				>
					Login
				</button>
				<a class="noreferrer" href="/join">
					<button
						on:click={() => {
							if (aboutToggle) aboutToggle = false;
						}}
						on:keyup={() => {
							if (aboutToggle) aboutToggle = false;
						}}
						class="nav-button mx-2 px-2 lg:px-3 py-2 text-base font-bold border-none bg-none cursor-pointer transition-all duration-300 hover:bg-cosmicMagenta400 lg:mx-0.5 lg:py-2 lg:text-sm xl:text-base 2xl:mx-1 lg:font-bold bg-cosmicMagenta400 rounded-lg text-white"
					>
						Apply
					</button>
				</a>
			{/if}
		</div>
	</nav>
{:else}
	<nav
		class="navbar-menu flex justify-end px-2 py-1 lg:w-[50%] ml-auto 4xl:w-[30%] xl:pr-6 rounded-l-full"
	>
		<!-- bg-gradient-to-r from-[#00A3B9] to-[#16E17F] opacity-90  -->
		<!-- Menu items -->
		<div class="hidden lg:flex items-center justify-center w-full font-black">
			<div class="relative" data-about-dropdown>
				<div
					class={navButtonWhite}
					on:click={toggleAbout}
					on:keyup={toggleAbout}
					role="button"
					aria-pressed="false"
					tabindex="0"
				>
					About
				</div>
				{#if aboutToggle}
					<div
						class="absolute top-14 bg-gradient-to-r from-[#00A3B9] to-[#16E17F] opacity-90 rounded-b-2xl"
					>
						<a
							on:click={toggleAbout}
							on:keyup={toggleAbout}
							class="block pb-2 {navButton}"
							href="/vision">Vision</a
						>
						<a
							on:click={toggleAbout}
							on:keyup={toggleAbout}
							class="block pb-2 {navButton}"
							href="/team">Team</a
						>
						<a
							on:click={toggleAbout}
							on:keyup={toggleAbout}
							class="block pb-2 {navButton}"
							href="/bloomers-without-borders">Volunteer</a
						>
						<a
							on:click={toggleAbout}
							on:keyup={toggleAbout}
							class="block pb-2 {navButton}"
							href="/donate">Donate</a
						>
					</div>
				{/if}
			</div>
			<a class="{navButtonWhite} " href="/engage/localBlooms">Local Blooms</a>
			<div class="grid grid-flow-row gap-2 text-left">
				{#if $user?.member}
					{#if $profileLoading}
						<!-- Loading state for profile -->
						<div
							class="mt-2 w-44 flex items-center justify-center border-none {topLink} {buttonBase}"
						>
							<div
								class="animate-spin rounded-full h-4 w-4 border-b-2 border-cosmicCyan mr-2"
							></div>
							<p class="mx-2 my-auto username text-sm">Loading...</p>
						</div>
					{:else if $profileStore.username}
						<button
							on:click={toggleProfile}
							on:keyup|preventDefault={(el) => {
								if (el.key === 'Enter') toggleProfile();
							}}
							data-profile-button
							class="mt-2 w-44 flex items-center justify-center border-none {topLink} {buttonBase} "
						>
							<p class="mx-2 my-auto username">
								{$profileStore.username}
							</p>
							<img
								class="w-8 h-8 ring-2 object-cover ring-bloomPurpleDark p-px rounded-full bg-bloomPurpleNormal bg-opacity-80"
								src={avatarUrl}
								alt={'avatar photo'}
							/>
						</button>
						<!-- Profile Dropdown Menu -->
						<div id="dropdownMenu" class="relative">
							{#if profileDropdownOpen}
								<ul
									class="dropdown-menu absolute top-full -mt-1 w-44 ml-4 bg-cosmicPurplebg border-none rounded-xl shadow-lg space-y-3 text-cosmicPurple100 px-2 md:px-6 pt-4 text-sm"
								>
									<li>
										<a
											class="text-cosmicPurple100"
											rel="no-refferer"
											href="/profile"
											on:click={toggleProfile}>Profile</a
										>
									</li>
									<li>
										<a
											class="text-cosmicPurple100"
											rel="no-refferer"
											href="/member/{$profileStore.username}"
											on:click={toggleProfile}>My Posts</a
										>
									</li>
									<li>
										<a
											class="text-cosmicPurple100"
											rel="no-refferer"
											href="/wallet"
											on:click={toggleProfile}>My Wallet</a
										>
									</li>
									<li>
										<button
											class=" text-cosmicPurple100 border-none -ml-4 -mt-4 on:click={toggleProfile}"
											on:click={onSignout}>Log Out</button
										>
									</li>
								</ul>
							{/if}
						</div>
					{:else}
						<a
							href="/profile"
							class="mt-2 w-52 flex items-center justify-center border-2 border-cosmicMagenta100 {topLink} {buttonBase}"
						>
							<p class="mx-2 my-auto username">Complete Your Profile</p>
						</a>
					{/if}
				{:else if $user && !$user?.member}
					<a
						href="/join"
						class="mt-2 w-44 flex items-center justify-center border-none {topLink} {buttonBase} "
					>
						<p class="mx-2 my-auto username">Finish Applying</p>
						<img
							class="w-8 h-8 ring-2 object-cover ring-bloomPurpleDark p-px rounded-full bg-bloomPurpleNormal bg-opacity-80"
							src={avatarUrl}
							alt={'avatar photo'}
						/>
					</a>
				{/if}
			</div>
			<a
				rel="no-refferer"
				href={'/member/notifications'}
				data-sveltekit-preload-data="hover"
				class="relative"
			>
				<img src="/img/daisy3.png" alt="daisy" class="w-10 mt-1" />
				{#if $unreadCount > 0}
					<span
						class="badge absolute top-2 right-0 translate-x-1/2 -translate-y-1/2 bg-[#ff00ff] text-white rounded-full text-base min-w-6 h-6 leading-6 px-2"
					>
						{$unreadCount}
					</span>
				{/if}
			</a>
		</div>
	</nav>
{/if}

<!-- <li class="grid grid-cols-5 items-center px-3 py-0 2xl:py-1 hover:bg-cosmicPurplebg">
					<svg
						id="b"
						xmlns="http://www.w3.org/2000/svg"
						width="21.58"
						height="22"
						role="button"
						aria-pressed="false"
						tabindex="0"
						class="mx-auto lg:scale-75 2xl:scale-100"
						on:click={onSignout}
						on:keyup|preventDefault={(el) => {
							if (el.key === 'Enter') onSignout();
						}}
						><defs
							><style>
								.d {
									fill: #a5b9f6;
									stroke-width: 0;
								}
							</style></defs
						><g id="c"
							><path
								class="d"
								d="M10.5 12.96c-1.08 0-1.96-.88-1.96-1.96s.89-1.96 1.96-1.96h3.96V2.79C14.46 1.25 13.2 0 11.67 0H2.79C1.25 0 0 1.25 0 2.79v16.42C0 20.75 1.25 22 2.79 22h8.88c1.53 0 2.79-1.25 2.79-2.79v-6.25H10.5Z"
							/><path
								class="d"
								d="m21.34 10.44-3.51-3.43c-.31-.3-.81-.3-1.11.01-.3.31-.3.81.01 1.11l2.13 2.08H10.5c-.43 0-.79.35-.79.79s.35.79.79.79h8.36l-2.13 2.08c-.31.3-.32.8-.01 1.11.15.16.36.24.56.24s.4-.07.55-.22l3.51-3.43c.07-.07.13-.16.17-.25a.815.815 0 0 0 0-.62c-.04-.1-.1-.18-.17-.25Z"
							/></g
						></svg
					>
					<div
						role="button"
						aria-pressed="false"
						tabindex="0"
						on:click={onSignout}
						on:keyup|preventDefault={(el) => {
							if (el.key === 'Enter') onSignout();
						}}
						class="block col-span-4 px-3 py-2 text-xs 2xl:text-base font-semibold 2xl:font-bold hover:bg-cosmicPurplebg"
					>
						Sign Out
					</div>
				</li> -->
<!-- <div
					role="button"
					aria-pressed="false"
					tabindex="0"
					on:click={toggleProfile}
					on:keyup|preventDefault={(el) => {
						if (el.key === 'Enter') toggleProfile();
					}}
					class="flex flex-row {topLink} {buttonBase} "
				>
					{#if $user?.member}
						<p class="mx-2 my-auto username">
							{$profileStore.username ? $profileStore.username : 'yourusername'}
						</p>
					{:else if $user && !$user?.member}
						<p class="mx-2 my-auto username">
							{formatNumber($user?.walletAddress)}
						</p>
					{/if}
					<img
						class="w-8 h-8 ring-2 object-cover ring-bloomPurpleDark p-px rounded-full bg-bloomPurple bg-opacity-80"
						src={avatarUrl}
						alt={'avatar photo'}
					/>
				</div> -->

<!-- <nav class="navbar-menu">
	<div class="hidden lg:block absolute right-10 xl:right-20 font-black -mt-6">
		<span class="flex align-middle text-center h-full w-full m-auto p-auto">
			<a data-sveltekit-preload-data="hover" href="/vision" class="{topLink} text-white uppercase"
				>vision</a
			>
			<a
				data-sveltekit-preload-data="hover"
				href="/engage/localBlooms"
				class="{topLink} {findButton} uppercase">Local Hubs</a
			>
			{#if !$user}
				<a
					href="/auth"
					data-sveltekit-preload-data="hover"
					class="{topLink} {buttonBase} bg-bloomPurpleNormal text-gray-100 mr-12 uppercase border-2 border-cosmicPurple600 rounded-2xl"
					>Login/Join</a
				>
			{:else}
				<a
					rel="no-refferer"
					href={'/profile'}
					data-sveltekit-preload-data="hover"
					on:click={toggleProfile}
				>
					<div
						role="button"
						aria-pressed="false"
						tabindex="0"
						on:click={toggleProfile}
						on:keyup|preventDefault={(el) => {
							if (el.key === 'Enter') toggleProfile();
						}}
						class="flex flex-row {topLink} {buttonBase} bg-bloomPurpleNormal text-gray-100 uppercase border-2 border-cosmicPurple600 rounded-2xl"
					>
						<p class="mx-2 my-auto">{username}</p>
						<img
							class="w-8 h-8 ring-2 object-cover ring-bloomPurpleDark p-px rounded-full bg-bloomPurpleLight bg-opacity-80"
							src={avatarUrl}
							alt={'avatar photo'}
						/>
					</div>
				</a>
				<a
					rel="no-refferer"
					href={'/member/notifications'}
					data-sveltekit-preload-data="hover"
					on:click={toggleProfile}
				>
					<img src="/img/daisy3.png" alt="daisy" class="w-10 mt-1" />
					<span
						class="inline-block absolute top-0 text-white right-0 rounded-full text-center text-base min-w-6 h-6 line-height-6 px-2"
					>
						{#if unreadCount > 0}
							<span class="badge">{unreadCount}</span>
						{/if}
					</span>
				</a>
			{/if}
		</span>
	</div>
</nav> -->

<style>
	/* custom width for message popup */
	.messages {
		min-width: 400px;
	}

	.username {
		color: #e5e7eb;
	}
</style>
